import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Button, CheckableTile, Column, Columns, Typography } from "@aviary";
import { setSessionData } from "@helpers/accessLocalStorage";
import { AnalyticsEvent, useAnalyticsLogger } from "@shared/hooks";
import { WellevateLoyalistMigrationEntryPoint } from "@shared/hooks/useAnalyticsLogger";
import { useSearchParams } from "@shared/react-router-dom";
import { l } from "@unauthenticated/wellevateAuthorize/practitioners/locales/i18n";
import { AccountCreationStep } from "@unauthenticated/wellevateAuthorize/practitioners/types";

import * as styles from "./WellevateLandingPage.styles";

const WellevateLandingPage = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const [isNewToFullscript, setIsNewToFullscript] = useState(null);
  const logAnalyticsEvent = useAnalyticsLogger();

  const handleOnClick = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.UNAUTH_MIGRATION_CONTINUE_MIGRATION,
      payload: {
        migration_option: isNewToFullscript
          ? WellevateLoyalistMigrationEntryPoint.NEW_TO_FULLSCRIPT
          : WellevateLoyalistMigrationEntryPoint.HAS_EXISTING_FULLSCRIPT_ACCOUNT,
      },
    });

    if (isNewToFullscript) {
      return setSearchParams({ step: AccountCreationStep.WELLEVATE_AUTH });
    }

    // This is to reset the checkbox in case we're coming back from a different SPA.
    // For whatever reason, the checkbox is still marked, unless we reset it explicitly
    setIsNewToFullscript(null);

    setSessionData("isFromLoyalistFlow", true);

    return window.location.assign("/");
  };

  return (
    <Arrange justify="center" alignment="center" spacing="none" css={styles.wrapper} isFullwidth>
      <Typography type="h2">{t(l.common.LandingPage.title)}</Typography>
      <Typography css={styles.subTitle}>{t(l.common.LandingPage.subTitle)}</Typography>
      <Columns css={styles.columns}>
        <Column columnWidth={6}>
          <CheckableTile
            checked={!!isNewToFullscript}
            onChange={() => {
              setIsNewToFullscript(true);
            }}
            aria-label={t(l.common.LandingPage.option1)}
            isRadio
            contentWrapperStyles={styles.tile}
          >
            <Typography type="h3">{t(l.common.LandingPage.option1)}</Typography>
          </CheckableTile>
        </Column>

        <Column columnWidth={6}>
          <CheckableTile
            checked={isNewToFullscript !== null && !isNewToFullscript}
            onChange={() => {
              setIsNewToFullscript(false);
            }}
            aria-label={t(l.common.LandingPage.option2)}
            isRadio
            contentWrapperStyles={styles.tile}
          >
            <Typography type="h3">{t(l.common.LandingPage.option2)}</Typography>
          </CheckableTile>
        </Column>
      </Columns>
      <div css={styles.buttonContainer}>
        <Button
          isColor="primary"
          onClick={handleOnClick}
          disabled={isNewToFullscript === null}
          css={styles.button}
        >
          {t(l.common.LandingPage.buttonLabel)}
        </Button>
      </div>
    </Arrange>
  );
};

export { WellevateLandingPage };
